import { useState } from "react";
import { callEdgeFunction } from "@/lib/rpc/utils/edgeFunctions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Shield, User, LogOut, Settings, RefreshCw, Bug, Database, Key } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils/error-display";
import { useSession } from "@/hooks/useSession";
import { useAdmin } from "@/contexts/AdminProvider";
import { useTranslation } from 'react-i18next';

import { AdminSecurity, AdminSystem, AdminWebhooks, AdminOperations } from '@/lib/rpc/admin-api';

const AdminGateDebug = () => {
  const { t } = useTranslation('common');
  const { status, user } = useSession();
  const { isAdmin: hookIsAdmin, isLoading: hookLoading } = useAdmin();
  
  const [sessionData, setSessionData] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [isAdminData, setIsAdminData] = useState<any>(null);
  const [bootstrapData, setBootstrapData] = useState<any>(null);
  const [qaPingData, setQaPingData] = useState<any>(null);
  const [envCheckData, setEnvCheckData] = useState<any>(null);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [directFetchResults, setDirectFetchResults] = useState<any>(null);
  
  // New admin testing data
  const [hookTestData, setHookTestData] = useState<any>(null);
  const [jwtVsRpcData, setJwtVsRpcData] = useState<any>(null);
  const [routeGuardData, setRouteGuardData] = useState<any>(null);
  const [dbRolesData, setDbRolesData] = useState<any>(null);

  const setLoadingState = (key: string, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const checkSession = async () => {
    console.log('[AdminGateDebug] Checking session...');
    setLoadingState('session', true);
    
    try {
      // Use facade for auth checks instead of direct client
      const sessionResult = await callEdgeFunction('auth-status');
      const session = sessionResult.data?.session;
      const user = sessionResult.data?.user;
      
      setSessionData(session);
      setUserData(user);
      
      console.log('[AdminGateDebug] Session check result:', { session, user });
      toast({ title: 'Session check completed', description: 'Results displayed below' });
    } catch (error: any) {
      console.error('[AdminGateDebug] Session check error:', error);
      setSessionData({ error: getErrorMessage(error) });
      setUserData({ error: getErrorMessage(error) });
      toast({ title: 'Session check failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('session', false);
    }
  };

  const checkIsAdmin = async () => {
    console.log('[AdminGateDebug] Checking admin status...');
    setLoadingState('admin', true);
    
    try {
      const result = await AdminSecurity.isAdmin();
      
      setIsAdminData({ data: result.data, error: result.error });
      console.log('[AdminGateDebug] Admin check result:', { data: result.data, error: result.error });
      toast({ 
        title: 'Admin check completed', 
        description: `Result: ${result.data ? 'Admin' : 'Not admin'}` 
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Admin check error:', error);
      setIsAdminData({ error: getErrorMessage(error) });
      toast({ title: 'Admin check failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('admin', false);
    }
  };

  const invokeAdminBootstrap = async () => {
    console.log('[AdminGateDebug] Invoking admin-bootstrap...');
    setLoadingState('bootstrap', true);
    
    try {
      const result = await callEdgeFunction('admin-bootstrap', {}, { 
        headers: { 'Content-Type': 'application/json' }
      });
      const { data, error } = { data: result.data, error: result.error };
      
      setBootstrapData({ data, error });
      console.log('[AdminGateDebug] Admin bootstrap result:', { data, error });
      toast({ 
        title: 'Bootstrap completed', 
        description: data?.message || 'Function executed' 
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Bootstrap error:', error);
      setBootstrapData({ error: getErrorMessage(error) });
      toast({ title: 'Bootstrap failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('bootstrap', false);
    }
  };

  const signOut = async () => {
    console.log('[AdminGateDebug] Signing out...');
    setLoadingState('signout', true);
    
    try {
      await callEdgeFunction('auth-signout');
      toast({ title: 'Signed out', description: 'You have been signed out successfully' });
      // Clear all data
      setSessionData(null);
      setUserData(null);
      setIsAdminData(null);
      setBootstrapData(null);
    } catch (error: any) {
      console.error('[AdminGateDebug] Sign out error:', error);
      toast({ title: 'Sign out failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('signout', false);
    }
  };

  const testQaPing = async () => {
    console.log('[AdminGateDebug] Testing QA ping...');
    setLoadingState('qaPing', true);
    
    try {
      const result = await callEdgeFunction('qa-ping', {});
      const { data, error } = { data: result.data, error: result.error };
      
      setQaPingData({ data, error });
      console.log('[AdminGateDebug] QA ping result:', { data, error });
      toast({ 
        title: 'QA ping completed', 
        description: data?.message || 'Ping executed' 
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] QA ping error:', error);
      setQaPingData({ error: getErrorMessage(error) });
      toast({ title: 'QA ping failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('qaPing', false);
    }
  };

  const directFetch = async () => {
    console.log('[AdminGateDebug] Direct fetch to functions...');
    setLoadingState('direct', true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://pacytsozkkqqsyscbvvm.supabase.co";
      const base = `${supabaseUrl}/functions/v1`;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBhY3l0c296a2txcXN5c2NidnZtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ4MzEwNTUsImV4cCI6MjA3MDQwNzA1NX0.HIS2eve8xmGiQCr9pti3ZdZqgYlHLGembKNRRs7-oN4";
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`
      };

      const urls = {
        qaPing: `${base}/qa-ping`,
        envCheck: `${base}/env-check`
      };

      const [qaRes, envRes] = await Promise.all([
        fetch(urls.qaPing, { method: 'GET' }).catch(e => e),
        fetch(urls.envCheck, { method: 'GET' }).catch(e => e),
      ]);

      const serialize = async (res: any) => {
        if (res instanceof Response) {
          let text = '';
          try {
            text = await res.text();
          } catch {
            /* response body not available */
          }
          return { ok: res.ok, status: res.status, url: res.url, text };
        } else {
          return { error: String(res) };
        }
      };

      setDirectFetchResults({
        urls,
        qaPing: await serialize(qaRes),
        envCheck: await serialize(envRes)
      });

      toast({ title: 'Direct fetch attempted', description: 'See results panel below' });
    } catch (error: any) {
      console.error('[AdminGateDebug] Direct fetch error:', error);
      setDirectFetchResults({ error: getErrorMessage(error) });
      toast({ title: 'Direct fetch failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('direct', false);
    }
  };
  const testEnvCheck = async () => {
    console.log('[AdminGateDebug] Testing environment check...');
    setLoadingState('envCheck', true);
    
    try {
      const result = await callEdgeFunction('env-check', {});
      const { data, error } = { data: result.data, error: result.error };
      
      setEnvCheckData({ data, error });
      console.log('[AdminGateDebug] Environment check result:', { data, error });
      toast({ 
        title: 'Environment check completed', 
        description: data?.summary?.overall_status || 'Check executed' 
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Environment check error:', error);
      setEnvCheckData({ error: getErrorMessage(error) });
      toast({ title: 'Environment check failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('envCheck', false);
    }
  };

  // New admin testing functions
  const testUseIsAdminReliableHook = async () => {
    console.log('[AdminGateDebug] Testing useIsAdminReliable hook...');
    setLoadingState('hookTest', true);
    
    try {
      const currentHookResult = {
        isAdmin: hookIsAdmin,
        isLoading: hookLoading,
        status: status,
        user_id: user?.id,
        user_email: user?.email
      };
      
      // Test current JWT-based logic (what hook actually does)
      const jwtRole = (user as any)?.app_role || (user as any)?.role;
      const jwtBasedResult = jwtRole === 'admin';
      
      // Test RPC-based logic (what hook should do)
      const { data: rpcResult, error: rpcError } = await AdminSecurity.isAdminSimple();
      
      const testResults = {
        current_hook_result: currentHookResult,
        jwt_based_check: {
          user_app_role: (user as any)?.app_role,
          user_role: (user as any)?.role,
          result: jwtBasedResult
        },
        rpc_based_check: {
          data: rpcResult,
          error: rpcError,
          result: rpcResult === true
        },
        problem_identified: jwtBasedResult !== (rpcResult === true),
        recommended_fix: 'Hook should use RPC instead of JWT roles'
      };
      
      setHookTestData(testResults);
      console.log('[AdminGateDebug] Hook test results:', testResults);
      
      toast({ 
        title: 'Hook test completed', 
        description: `Problem: ${testResults.problem_identified ? 'FOUND' : 'None'}` 
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Hook test error:', error);
      setHookTestData({ error: getErrorMessage(error) });
      toast({ title: 'Hook test failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('hookTest', false);
    }
  };

  const testJwtVsRpcComparison = async () => {
    console.log('[AdminGateDebug] Testing JWT vs RPC comparison...');
    setLoadingState('jwtVsRpc', true);
    
    try {
      // Get current user session
      const { data: { session }, error: sessionError } = await (await import('@/integrations/supabase/client')).supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('No active session found');
      }
      
      const user = session.user;
      
      // JWT-based checks
      const jwtData = {
        user_id: user.id,
        email: user.email,
        app_metadata: user.app_metadata,
        user_metadata: user.user_metadata,
        app_role: user.app_metadata?.app_role,
        role: user.role,
        jwt_admin_check: user.app_metadata?.app_role === 'admin' || user.role === 'admin'
      };
      
      // RPC-based checks
      const { data: rpcSimple, error: rpcSimpleError } = await AdminSecurity.isAdminSimple();
      const { data: rpcHasRole, error: rpcHasRoleError } = await AdminSecurity.hasRole(user.id, 'admin');
      
      const comparison = {
        jwt_data: jwtData,
        rpc_checks: {
          is_admin_simple: { data: rpcSimple, error: rpcSimpleError },
          has_admin_role: { data: rpcHasRole, error: rpcHasRoleError }
        },
        comparison: {
          jwt_says_admin: jwtData.jwt_admin_check,
          rpc_says_admin: rpcSimple === true,
          results_match: jwtData.jwt_admin_check === (rpcSimple === true),
          problem_summary: jwtData.jwt_admin_check !== (rpcSimple === true) 
            ? 'JWT and RPC disagree - this is the root cause!'
            : 'JWT and RPC agree - no authentication issue'
        }
      };
      
      setJwtVsRpcData(comparison);
      console.log('[AdminGateDebug] JWT vs RPC comparison:', comparison);
      
      toast({ 
        title: 'JWT vs RPC comparison completed', 
        description: comparison.comparison.results_match ? 'Results match' : 'Results DISAGREE - Problem found!'
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] JWT vs RPC comparison error:', error);
      setJwtVsRpcData({ error: getErrorMessage(error) });
      toast({ title: 'JWT vs RPC comparison failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('jwtVsRpc', false);
    }
  };

  const testRouteGuardAdminLogic = async () => {
    console.log('[AdminGateDebug] Testing RouteGuard(admin) logic...');
    setLoadingState('routeGuard', true);
    
    try {
      // Simulate RouteGuard(admin) logic
      const sessionStatus = status;
      const currentUser = user;
      const hookResult = hookIsAdmin;
      const hookIsAdminLoading = hookLoading;
      
      // Test what RouteGuard(admin) would do
      let guardDecision = '';
      let redirectTo = '';
      
      if (sessionStatus === 'loading' || hookIsAdminLoading) {
        guardDecision = 'LOADING';
        redirectTo = 'show loading spinner';
      } else if (sessionStatus === 'anon') {
        guardDecision = 'REDIRECT_TO_AUTH';
        redirectTo = '/auth';
      } else if (!hookResult) {
        guardDecision = 'ACCESS_DENIED';
        redirectTo = '/admin-access-denied';
      } else {
        guardDecision = 'ACCESS_GRANTED';
        redirectTo = 'render admin content';
      }
      
      // Also test direct RPC for comparison
      const { data: directRpcResult } = await AdminSecurity.isAdminSimple();
      
      const guardTest = {
        session_status: sessionStatus,
        user_authenticated: !!currentUser,
        user_id: currentUser?.id,
        hook_is_admin: hookResult,
        hook_loading: hookLoading,
        guard_decision: guardDecision,
        redirect_to: redirectTo,
        direct_rpc_check: directRpcResult,
        would_access_be_granted: guardDecision === 'ACCESS_GRANTED',
        problem_analysis: {
          hook_blocks_access: guardDecision === 'ACCESS_DENIED',
          but_rpc_says_admin: directRpcResult === true,
          this_is_the_bug: guardDecision === 'ACCESS_DENIED' && directRpcResult === true
        }
      };
      
      setRouteGuardData(guardTest);
      console.log('[AdminGateDebug] Route guard test:', guardTest);
      
      toast({ 
        title: 'Route guard test completed', 
        description: guardTest.problem_analysis.this_is_the_bug 
          ? 'BUG CONFIRMED: Hook blocks but RPC allows!' 
          : 'No route guard issue detected'
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Route guard test error:', error);
      setRouteGuardData({ error: getErrorMessage(error) });
      toast({ title: 'Route guard test failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('routeGuard', false);
    }
  };

  const checkDatabaseRoles = async () => {
    console.log('[AdminGateDebug] Checking database roles...');
    setLoadingState('dbRoles', true);
    
    try {
      if (!user?.id) {
        throw new Error('No user ID available');
      }
      
      // Get user roles via admin RPC function
      let userRolesResult;
      try {
        userRolesResult = await (await import('@/integrations/supabase/client')).supabase
          .rpc('admin_get_user_roles', { _user_id: user.id });
      } catch (e) {
        userRolesResult = { data: null, error: { message: 'Admin RPC access failed' } };
      }
      
      // Test various admin check functions
      const { data: isAdminSimple, error: isAdminSimpleError } = await AdminSecurity.isAdminSimple();
      const { data: hasAdminRole, error: hasAdminRoleError } = await AdminSecurity.hasRole(user.id, 'admin');
      
      const dbCheck = {
        user_id: user.id,
        email: user.email,
        direct_table_query: userRolesResult,
        rpc_functions: {
          is_admin_simple: { data: isAdminSimple, error: isAdminSimpleError },
          has_admin_role: { data: hasAdminRole, error: hasAdminRoleError }
        },
        summary: {
          can_access_user_roles_table: !userRolesResult.error,
          admin_via_is_admin_simple: isAdminSimple === true,
          admin_via_has_role: hasAdminRole === true,
          consistent_results: isAdminSimple === hasAdminRole
        }
      };
      
      setDbRolesData(dbCheck);
      console.log('[AdminGateDebug] Database roles check:', dbCheck);
      
      toast({ 
        title: 'Database roles check completed', 
        description: `Admin status: ${dbCheck.summary.admin_via_is_admin_simple ? 'YES' : 'NO'}`
      });
    } catch (error: any) {
      console.error('[AdminGateDebug] Database roles check error:', error);
      setDbRolesData({ error: getErrorMessage(error) });
      toast({ title: 'Database roles check failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoadingState('dbRoles', false);
    }
  };

  const clearData = () => {
    setSessionData(null);
    setUserData(null);
    setIsAdminData(null);
    setBootstrapData(null);
    setQaPingData(null);
    setEnvCheckData(null);
    setHookTestData(null);
    setJwtVsRpcData(null);
    setRouteGuardData(null);
    setDbRolesData(null);
    console.log('[AdminGateDebug] Data cleared');
    toast({ title: 'Data cleared', description: 'All debug data has been cleared' });
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Admin Gate Debug
            </CardTitle>
            <CardDescription>
              Diagnostic tools for troubleshooting admin access issues.
              This page bypasses all authentication checks for debugging purposes.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Live Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Current Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <strong>Session:</strong> {status}
              </div>
              <div>
                <strong>User:</strong> {user?.email || 'None'}
              </div>
              <div>
                <strong>Hook Says Admin:</strong> <span className={hookIsAdmin ? 'text-success' : 'text-destructive'}>
                  {hookLoading ? t('loading', 'Loading...') : (hookIsAdmin ? 'YES' : 'NO')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Problem Testing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Admin Access Problem Testing
            </CardTitle>
            <CardDescription>
              Test the specific issue with useIsAdminReliable hook and admin access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button 
                onClick={testUseIsAdminReliableHook} 
                disabled={loading.hookTest}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Bug className="h-4 w-4" />
                {loading.hookTest ? 'Testing...' : 'Test useIsAdminReliable Hook'}
              </Button>
              
              <Button 
                onClick={testJwtVsRpcComparison} 
                disabled={loading.jwtVsRpc}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Key className="h-4 w-4" />
                {loading.jwtVsRpc ? 'Comparing...' : 'JWT vs RPC Comparison'}
              </Button>
              
              <Button 
                onClick={testRouteGuardAdminLogic}
                disabled={loading.routeGuard}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Shield className="h-4 w-4" />
                {loading.routeGuard ? 'Testing...' : 'Test Route Guard Logic'}
              </Button>
              
              <Button 
                onClick={checkDatabaseRoles} 
                disabled={loading.dbRoles}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Database className="h-4 w-4" />
                {loading.dbRoles ? 'Checking...' : 'Check Database Roles'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Standard Debug Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Standard Debug Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button 
                onClick={checkSession} 
                disabled={loading.session}
                className="flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                {loading.session ? 'Checking...' : 'Check Session'}
              </Button>
              
              <Button 
                onClick={checkIsAdmin} 
                disabled={loading.admin}
                className="flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                {loading.admin ? 'Checking...' : 'Check Admin'}
              </Button>
              
              <Button 
                onClick={invokeAdminBootstrap} 
                disabled={loading.bootstrap}
                className="flex items-center gap-2"
                variant="secondary"
              >
                <Settings className="h-4 w-4" />
                {loading.bootstrap ? 'Calling...' : 'Bootstrap Admin'}
              </Button>
              
              <Button 
                onClick={testQaPing} 
                disabled={loading.qaPing}
                className="flex items-center gap-2"
                variant="outline"
              >
                <AlertTriangle className="h-4 w-4" />
                {loading.qaPing ? 'Testing...' : 'QA Ping'}
              </Button>
              
              <Button 
                onClick={testEnvCheck} 
                disabled={loading.envCheck}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Settings className="h-4 w-4" />
                {loading.envCheck ? 'Checking...' : 'Env Check'}
              </Button>

              <Button 
                onClick={directFetch} 
                disabled={loading.direct}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Settings className="h-4 w-4" />
                {loading.direct ? 'Fetching...' : 'Direct Fetch'}
              </Button>
              
              <Button 
                onClick={signOut} 
                disabled={loading.signout}
                className="flex items-center gap-2"
                variant="destructive"
              >
                <LogOut className="h-4 w-4" />
                {loading.signout ? 'Signing out...' : 'Sign Out'}
              </Button>
            </div>
            
            <div className="mt-4 flex justify-center">
              <Button 
                onClick={clearData} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Clear Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Admin Problem Test Results */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hook Test Results */}
          {hookTestData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bug className="h-5 w-5" />
                  useIsAdminReliable Hook Test
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hookTestData.problem_identified && (
                  <div className="mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <strong>PROBLEM FOUND!</strong>
                    </div>
                    <div className="text-sm text-destructive mt-1">
                      JWT says: {hookTestData.jwt_based_check.result ? 'Admin' : 'Not Admin'} | 
                      RPC says: {hookTestData.rpc_based_check.result ? 'Admin' : 'Not Admin'}
                    </div>
                  </div>
                )}
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(hookTestData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* JWT vs RPC Comparison */}
          {jwtVsRpcData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  JWT vs RPC Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                {jwtVsRpcData.comparison && !jwtVsRpcData.comparison.results_match && (
                  <div className="mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <strong>ROOT CAUSE IDENTIFIED!</strong>
                    </div>
                    <div className="text-sm text-destructive mt-1">
                      {jwtVsRpcData.comparison.problem_summary}
                    </div>
                  </div>
                )}
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(jwtVsRpcData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Route Guard Logic Test */}
          {routeGuardData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Route Guard Logic Test
                </CardTitle>
              </CardHeader>
              <CardContent>
                {routeGuardData.problem_analysis?.this_is_the_bug && (
                  <div className="mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <strong>BUG CONFIRMED!</strong>
                    </div>
                    <div className="text-sm text-destructive mt-1">
                      Hook blocks access but RPC says user is admin - this is why you can't access /admin
                    </div>
                  </div>
                )}
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(routeGuardData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Database Roles Check */}
          {dbRolesData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Database Roles Check
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dbRolesData.summary && (
                  <div className={`mb-4 p-3 rounded-md ${
                    dbRolesData.summary.admin_via_is_admin_simple 
                      ? 'bg-success/5 border border-success/20' 
                      : 'bg-warning/5 border border-warning/20'
                  }`}>
                    <div className={`font-medium ${
                      dbRolesData.summary.admin_via_is_admin_simple ? 'text-success' : 'text-warning'
                    }`}>
                      Database says: {dbRolesData.summary.admin_via_is_admin_simple ? '✅ Admin' : '❌ Not Admin'}
                    </div>
                  </div>
                )}
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(dbRolesData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Standard Results Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Session Data */}
          {sessionData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Session Data</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(sessionData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* User Data */}
          {userData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">User Data</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(userData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Admin Check Data */}
          {isAdminData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Admin Check Result</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(isAdminData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Bootstrap Data */}
          {bootstrapData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bootstrap Result</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(bootstrapData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* QA Ping Data */}
          {qaPingData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">QA Ping Result</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(qaPingData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Environment Check Data */}
          {envCheckData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Environment Check Result</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(envCheckData, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Direct Fetch Results */}
          {directFetchResults && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Direct Fetch Results</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(directFetchResults, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Navigation */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-4">
              <Button 
                onClick={() => window.location.href = '/admin'}
                variant="outline"
              >
                Try Admin Panel
              </Button>
              <Button 
                onClick={() => window.location.href = '/auth'}
                variant="outline"
              >
                Go to Auth
              </Button>
              <Button 
                onClick={() => window.location.href = '/dashboard'}
                variant="outline"
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminGateDebug;